// Utilities for Activity Level features

export function normalizeModuleKey(name) {
  if (!name) return 'unknown';
  const s = String(name).toLowerCase().replace(/[^a-z0-9]/g, '');
  if (s.startsWith('inspectiondashboard')) return 'inspectiondashboards';
  if (s.includes('dashboard') && s.includes('ava')) return 'askavadashboards';
  if (s.includes('dashboard')) return 'customdashboards';
  if (s.includes('corrosion')) return 'corrosionai';
  if (s.includes('askava') || s === 'ava' || s.includes('ask')) return 'askava';
  if (s.includes('viewer') || s === 'view') return 'viewer';
  if (s.includes('inspection')) return 'inspection';
  if (s.includes('task') || s.includes('kanban')) return 'taskboards';
  if (s.includes('download') || s.includes('scan')) return 'scandownloads';
  return s;
}

export function getModuleIcon(key) {
  const map = {
    inspection: 'mdi-magnify-scan',
    corrosionai: 'mdi-robot-outline',
    askava: 'mdi-account-question-outline',
    viewer: 'mdi-eye-outline',
    askavadashboards: 'mdi-view-dashboard-outline',
    inspectiondashboards: 'mdi-view-dashboard-outline',
    taskboards: 'mdi-view-kanban',
    customdashboards: 'mdi-view-dashboard-variant-outline',
    scandownloads: 'mdi-download',
  };
  return map[normalizeModuleKey(key)] || 'mdi-chart-bar';
}

export function getDayKeysBetween(start, end) {
  if (!start || !end) return [];
  const s = new Date(start);
  const e = new Date(end);
  if (isNaN(s) || isNaN(e)) return [];
  const pad = (n) => String(n).padStart(2, '0');
  const keys = [];
  for (let dt = new Date(s.getFullYear(), s.getMonth(), s.getDate()); dt <= e; dt.setDate(dt.getDate() + 1)) {
    const y = dt.getFullYear();
    const m = pad(dt.getMonth() + 1);
    const d = pad(dt.getDate());
    keys.push(`${y}-${m}-${d}`);
  }
  return keys;
}

export function getRangeForKey(key) {
  const today = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const startOfWeek = (d) => {
    const day = d.getDay();
    const diff = (day === 0 ? -6 : 1) - day; // Monday as first day
    const nd = new Date(d);
    nd.setDate(d.getDate() + diff);
    return startOfDay(nd);
  };
  const endOfWeek = (d) => {
    const sow = startOfWeek(d);
    const e = new Date(sow);
    e.setDate(sow.getDate() + 6);
    return endOfDay(e);
  };
  const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
  const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);
  const endOfYear = (d) => new Date(d.getFullYear(), 12, 0);
  switch (key) {
    case 'today':
      return { start: startOfDay(today), end: endOfDay(today) };
    case 'this_week':
      return { start: startOfWeek(today), end: endOfWeek(today) };
    case 'this_month':
      return { start: startOfMonth(today), end: endOfMonth(today) };
    case 'prev_month': {
      const d = new Date(today.getFullYear(), today.getMonth() - 1, 15);
      return { start: startOfMonth(d), end: endOfMonth(d) };
    }
    case 'this_year':
      return { start: startOfYear(today), end: endOfYear(today) };
    default:
      return { start: startOfMonth(today), end: endOfMonth(today) };
  }
}

export function formatDate(d) {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function getNumericSeries(series) {
  if (!Array.isArray(series)) return [];
  if (series.length && typeof series[0] === 'number') {
    return series.map((n) => Number(n) || 0);
  }
  const valueKeys = ['value', 'v', 'y', 'val', 'score', 'level'];
  return series.map((p) => {
    if (typeof p === 'number') return Number(p) || 0;
    if (p && typeof p === 'object') {
      for (const k of valueKeys) {
        if (isFinite(p[k])) return Number(p[k]) || 0;
      }
    }
    return 0;
  });
}

export function normalizeBarSeries(series, maxLen = 24) {
  if (!Array.isArray(series)) return [];
  const cleaned = series
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v))
    .map((v) => Math.max(0, Math.round(v)));
  return cleaned.length > maxLen ? cleaned.slice(cleaned.length - maxLen) : cleaned;
}

export function extractUserName(item) {
  if (!item || typeof item !== 'object') return '';
  const nameCandidates = [
    item.userName,
    item.username,
    item.user,
    item.createdByName,
    item.createdBy,
    item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : null,
    item.email,
    item.userEmail,
  ];
  const name = nameCandidates.find((v) => typeof v === 'string' && v.trim() !== '');
  return name ? String(name).trim() : '';
}

export function extractUserKey(item) {
  if (!item || typeof item !== 'object') return 'unknown';
  const candidates = [
    item.userId,
    item.userID,
    item.user_id,
    item.createdById,
    item.createdByUserId,
    item.ownerId,
    item.userGuid,
    item.userGUID,
    item.userid,
  ];
  const foundId = candidates.find((v) => v !== undefined && v !== null && String(v).trim() !== '');
  if (foundId !== undefined) return String(foundId);
  const name = extractUserName(item);
  return name || 'unknown';
}

export function getModuleLabelFromKey(key, activityModules) {
  const norm = normalizeModuleKey(key);
  const found = (activityModules || []).find((m) => normalizeModuleKey(m.key) === norm);
  return (found && (found.label || found.key)) || (typeof key === 'string' ? key : 'Module');
}
